import React, { useContext, useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styles from "./styles.module.scss";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import CloseIcon from "assets/icons/close.svg?react";
import PersonIcon from "assets/icons/company/person.svg?react";
import CompanyIcon from "assets/icons/company/company.svg?react";
import { ETabs, FC, Props } from "./typings";
import Button from "components/Button";
import { ENotificationType, notification } from "utils/notification";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { ITag } from "dto/ITag";
import PersonForm from "./PersonForm";
import CompanyForm from "./CompanyForm";
import TagBadge from "components/TagBadge";
import clientService, {
  ICreateClient,
  INewUpdateClient,
} from "api/clientService";
import ClientsContext from "contexts/ClientsContext";
import { validateEmail } from "utils/isEmail";
import { IPackage, ISenderDetails } from "dto/IPackage";
import { axiosInstance } from "api/axios";
import SearchLabel from "components/SearchLabel";
import ClientAdditionalAccordion from "components/ClientAdditionalAccordion";
import SignalsContext from "contexts/SignalsContext";
import DownIcon from "assets/icons/arrow-down.svg?react";
import { IClient, IClientData } from "dto/IClient";
import ModalsManagerContext, {
  ClientModalOptions,
} from "contexts/ModalsManager";
import { useTranslation } from "react-i18next";
import Loading from "components/Loading";
import { getErrorMessage } from "utils/getErrorMessage";
import { toClientData, toFormData } from "./clientData";
import { isValidPesel } from "utils/isValidPesel";

export interface IAddress {
  street: string;
  zipCode: string;
  city: string;
  address?: string;
  /** Wartość z bazy (JSON) — niezmieniony adres wraca bez zmian, zmieniony zachowuje pozostałe pola. */
  raw?: string;
}

export interface ICPerson {
  name: string;
  email: string;
  phone: string;
}

export interface IData {
  Case: ICaseSimple | null;
  personNip: string | null;
  personRegon: string | null;
  nip: string | null;
  regon: string | null;
  name: string;
  emails: string[];
  phones: string[];
  EAddresses: string[];
  bank: string[];
  addresses: IAddress[];
  note: string | null;
  contactPerson: ICPerson[];
  tagIds: ITag[];
  krs?: string | null;
  /** Nowy PESEL wpisany w formularzu (pusty = bez zmian). */
  pesel?: string;
  /** Podmiot ma zapisany PESEL (flaga HasPesel) — wartości API nie zwraca, nawet zamaskowanej. */
  hasSavedPesel?: boolean;
  /** Użytkownik usunął zapisany PESEL. */
  peselRemoved?: boolean;
  /** Dane podmiotu z chwili otwarcia edycji. */
  original?: IClientData[];
  /** Dane, których formularz nie edytuje (osoby kontaktowe, skrytka ePUAP…) — zapis odsyła je bez zmian. */
  preserved?: IClientData[];
}

const EditClientModalNew: FC<Props> = ({
  onClose,
  onCreate,
  isCompany = false,
}) => {
  const { t } = useTranslation();
  const [initialValue, setInitialValue] = useState<IClient | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const { options } = useContext(ModalsManagerContext);

  const [actualView, setActualView] = useState(
    isCompany ? ETabs.COMPANY : ETabs.PERSON
  );
  const { mutate: createClient, isLoading: isL } =
    clientService.useCreateClient();
  const { mutate: updateClient, isLoading: isL2 } =
    clientService.useNewUpdateClient();

  // Edycja zawsze z pełnych danych podmiotu (GET /client) — wpis z listy albo wyszukiwarki
  // bywa niepełny, a zapis zastępuje komplet danych.
  const { data: clientData, isError: clientError } = clientService.useGetClient(
    clientId ?? "",
    clientId !== null
  );
  // Wpis przekazany do edycji z listy — tylko gdy pełnych danych nie da się pobrać.
  const [fallbackValue, setFallbackValue] = useState<IClient | null>(null);
  const isLoading = isL || isL2;
  const { setCallback } = useContext(SignalsContext);
  const { clients, setClients } = useContext(ClientsContext);

  useEffect(() => {
    // domyślne opcje to {} — bez ClientId okno tworzy nowy podmiot
    const id = (options as ClientModalOptions | undefined)?.ClientId;
    if (id != null) {
      setClientId(id);
    }
  }, [options]);

  useEffect(() => {
    if (clientData?.data) {
      setInitialValue(clientData.data);
    }
  }, [clientData]);

  useEffect(() => {
    if (clientError && fallbackValue) {
      setInitialValue(fallbackValue);
    }
  }, [clientError, fallbackValue]);

  const loadingClient = clientId !== null && !initialValue && !clientError;
  const isEdit = !!initialValue || clientId !== null;

  const [data, setData] = useState<IData>({
    Case: null,
    personNip: null,
    personRegon: null,
    nip: "",
    regon: "",
    name: "",
    emails: ["", ""],
    phones: ["", ""],
    EAddresses: ["", ""],
    bank: [""],
    addresses: [{ city: "", street: "", zipCode: "", address: "" }],
    contactPerson: [{ email: "", name: "", phone: "" }],
    note: "",
    tagIds: [],
  });

  useEffect(() => {
    if (!initialValue) return;
    setActualView(initialValue.IsInstitution ? ETabs.COMPANY : ETabs.PERSON);
    setData((prev) => toFormData(initialValue, prev));
  }, [initialValue]);

  useEffect(() => {
    const item = localStorage.getItem("toEdit");
    if (!item) return;
    localStorage.removeItem("toEdit");
    const client: IClient = JSON.parse(item);
    if (client?.Id) {
      setFallbackValue(client);
      setClientId(client.Id);
    } else {
      setInitialValue(client);
    }
  }, []);

  const handleCreate = () => {
    const isInstitution = actualView === ETabs.COMPANY;
    const clean = (value?: string | null) =>
      (value ?? "").replaceAll(" ", "") || null;
    // NIP ma osobne pole na każdej zakładce; REGON jest jeden — w danych dodatkowych.
    const nip = clean(isInstitution ? data.nip : data.personNip);
    const regon = clean(data.regon);
    const name = data.name.replaceAll("<", "").replaceAll(">", "");
    // PESEL tylko dla osoby: wpisany = zapis/zmiana, „Usuń” = pusty napis, nic = bez zmian
    const pesel = data.pesel?.trim() ?? "";
    const peselChange = isInstitution
      ? null
      : pesel.length
        ? pesel
        : data.peselRemoved
          ? ""
          : null;

    if (initialValue) {
      const payload: INewUpdateClient = {
        Id: initialValue.Id,
        Name: name,
        Data: toClientData(data),
        IsInstitution: isInstitution,
        NIP: nip,
        REGON: regon,
        // formularz nie edytuje KRS — zapis bez niego wyczyściłby go w bazie
        KRS: data.krs ?? initialValue.KRS ?? null,
        TagIds: data.tagIds.map((t) => t.Id),
        Pesel: peselChange,
      };

      updateClient(payload, {
        onSuccess: ({ data: saved }) => {
          setClients(
            clients.map((c) =>
              c.Id === saved.Id
                ? {
                    ...c,
                    Name: saved.Name,
                    NIP: saved.NIP,
                    REGON: saved.REGON,
                    KRS: saved.KRS,
                    IsInstitution: saved.IsInstitution,
                    Data: saved.Data,
                    Flags: saved.Flags,
                  }
                : c
            )
          );
          onClose();
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: t("columns.entity"),
            text: getErrorMessage(e) || t("errors.somethingWentWrongShort"),
          }),
      });
      return;
    }

    const payload: ICreateClient = {
      Name: name,
      Data: toClientData(data),
      IsInstitution: isInstitution,
      TagIds: data.tagIds.map((t) => t.Id),
      NIP: nip,
      REGON: regon,
      Pesel: peselChange || null,
    };

    createClient(payload, {
      onSuccess: (resp) => {
        setClients([resp.data, ...clients]);
        onCreate?.(resp.data);
        onClose();
        console.log("ustawiam: ", resp.data);
        setCallback(resp.data);
      },
      onError: (resp: any) => {
        if (resp?.response?.data?.errorCodes?.[0]?.code === "Duplicate") {
          notification({
            type: ENotificationType.ERROR,
            title: t("columns.entity"),
            text: t("client.entityDuplicateError"),
          });
          return;
        }
        notification({
          type: ENotificationType.ERROR,
          title: t("columns.entity"),
          text: t("errors.somethingWentWrongShort"),
        });
      },
    });
  };

  const checkCan = () => {
    const wrongEmails = data.emails?.filter(
      (f) => !!f.length && !validateEmail(f)
    );

    // przed wczytaniem podmiotu zapis utworzyłby nowy zamiast poprawić istniejący
    if (loadingClient) return true;

    if (!data?.name?.length) return true;

    if (wrongEmails?.length) {
      return true;
    }

    if (
      actualView === ETabs.PERSON &&
      !!data.pesel?.length &&
      !isValidPesel(data.pesel)
    ) {
      return true;
    }

    return false;
  };

  const refWrapper = useRef(null);

  const [entities, setEntities] = useState([]);

  const isPackages = window.location.href.includes("incoming");

  // „Pobierz dane” z otwartej przesyłki przychodzącej — tylko na jej stronie (adres URL niesie Id).
  useEffect(() => {
    if (!isPackages) return;
    (async () => {
      try {
        const link = window.location.href.split("/");
        const isThread = window.location.href.includes("thread");
        const packageId = isThread ? link[9] : link[7];
        const packageKind = isThread ? link[7] : link[6];
        const response = await axiosInstance.get(
          `/list?Id=${packageId}&Kind=${packageKind}`
        );

        const pack: IPackage | undefined = response.data?.Items?.[0];
        if (!pack) return;
        const details: ISenderDetails = pack.SenderDetails;

        setEntities([pack.Receivers, details].flat().filter(Boolean));
      } catch {
        // bez danych przesyłki „Pobierz dane” po prostu nic nie podpowie
      }
    })();
  }, [isPackages]);

  const fetchData = async (index: number) => {
    const details = entities[index];
    const newData = { ...data };

    if (details?.IsInstitution) {
      setActualView(ETabs.COMPANY);
    }

    if (details?.Name) {
      newData.name = details.Name;
    }

    if (details?.Email) {
      newData.emails[0] = details.Email;
    }

    if (details?.Nip) {
      newData.nip = details.Nip;
      newData.personNip = details.Nip;
    }
    if (details?.REGON) {
      newData.regon = details.REGON;
      newData.personRegon = details.REGON;
    }

    if (details?.Address) {
      newData.addresses[0] = {
        city: details.Address.city,
        street: details.Address.address ?? "",
        zipCode: details.Address.zipCode,
      };
    }

    newData.bank = [""];

    setData(newData);
  };

  const handleAdd = (
    type: string,
    regon?: string,
    place?: any,
    name?: string
  ) => {
    switch (type) {
      case "all":
        // dane z GUS: nazwa, REGON i adres — pozostałe dane podmiotu zostają
        setData({
          ...data,
          name: name ? name : data.name ?? "",
          note: data.note ?? "",
          contactPerson: data.contactPerson[0]
            ? [...data.contactPerson]
            : [{ email: "", name: "", phone: "" }],

          addresses: [
            ...data.addresses,
            place ?? { city: "", street: "", zipCode: "" },
          ].filter((e, index) => index === 0 || !!e.city.length),
          regon: regon ?? "",
          personRegon: regon ?? "",
        });

        return;

      case "email":
        setData({ ...data, emails: [...data.emails, ""] });
        return;
      case "phone":
        setData({ ...data, phones: [...data.phones, ""] });
        return;
      case "bank":
        setData({ ...data, bank: [...data.bank, ""] });
        return;
      case "eaddress":
        setData({ ...data, EAddresses: [...data.EAddresses, ""] });
        return;
      case "note":
        setData({ ...data, note: "" });
        return;
      case "person":
        setData({
          ...data,
          contactPerson: [
            ...data.contactPerson,
            { email: "", name: "", phone: "" },
          ],
        });
        return;
      case "address":
        setData({
          ...data,
          addresses: [...data.addresses, { city: "", street: "", zipCode: "" }],
        });
        return;
      case "nip":
        setData({ ...data, personNip: "" });
        return;
      case "regon":
        setData({ ...data, personRegon: "" });
        return;
    }
  };

  const [additionalData, setAdditionalData] = useState(false);

  return ReactDOM.createPortal(
    <ModalLayout
      name="createEntity"
      dock={DockPosition.DISABLED}
      off={true}
      customX={8}
      customY={0}
      isAnimation
      ignore
      refWrapper={refWrapper}
      fullHeight={true}
    >
      <div className={styles.wrapper}>
        <div className={styles.title}>
          {isEdit ? t("client.editEntity") : t("client.newEntity")}
          <CloseIcon onClick={onClose} className={styles.closeIcon} />
        </div>
        {loadingClient ? (
          <div className={styles.loading}>
            <Loading />
          </div>
        ) : (
        <div className={styles.content}>
          <div className={styles.switchbox}>
            <div
              onClick={() => setActualView(ETabs.PERSON)}
              className={`${styles.switch}  ${
                actualView === ETabs.PERSON && styles.active
              }`}
            >
              <PersonIcon /> {t("common.person")}
            </div>
            <div
              onClick={() => setActualView(ETabs.COMPANY)}
              className={`${styles.switch}  ${
                actualView === ETabs.COMPANY && styles.active
              }`}
            >
              <CompanyIcon /> {t("client.company")}
            </div>
          </div>
          {actualView === ETabs.PERSON && (
            <div className={styles.formW}>
              <PersonForm
                fetchData={fetchData}
                handleAdd={handleAdd}
                data={data}
                setData={setData}
                isPackages={isPackages}
                entities={entities}
              />
            </div>
          )}
          {actualView === ETabs.COMPANY && (
            <div className={styles.formW}>
              <CompanyForm
                fetchData={fetchData}
                handleAdd={handleAdd}
                data={data}
                setData={setData}
                isPackages={isPackages}
                entities={entities}
              />
            </div>
          )}

          <div className={styles.menus}>
            <SearchLabel
              label={t("forms.role")}
              type={3}
              onChoose={(tag) => {
                setData({ ...data, tagIds: [...data.tagIds, tag!] });
              }}
            />
            {/* {isPackages && (
              <div onClick={fetchData} className={styles.fetch}>
                <Dicon /> Pobierz dane z przesyłki
              </div>
            )} */}
          </div>
          {!!data.tagIds.length && (
            <div className={styles.tags}>
              {data.tagIds.map((t) => (
                <TagBadge
                  key={t.Id}
                  tag={t}
                  onRemove={() => {
                    setData({
                      ...data,
                      tagIds: data.tagIds.filter((te) => te.Id !== t.Id),
                    });
                  }}
                />
              ))}
            </div>
          )}
          <div className={styles.accordion}>
            <div
              onClick={() => setAdditionalData(!additionalData)}
              className={styles.addTitle}
            >
              {t("client.additionalData")}{" "}
              <DownIcon
                className={`${styles.ico} ${additionalData && styles.reverse}`}
              />
            </div>
            <ClientAdditionalAccordion
              handleAdd={handleAdd}
              data={data}
              showed={additionalData}
              setData={setData}
            />
          </div>
        </div>
        )}
        <div className={styles.footer}>
          <Button
            loading={isLoading}
            disabled={checkCan()}
            onClick={handleCreate}
            text={initialValue ? t("common.save") : t("common.create")}
          />
        </div>
      </div>
    </ModalLayout>,
    document.body
  );
};

export default EditClientModalNew;
